import React from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { AlertTriangle, FileUp, X, Loader2 } from 'lucide-react';
import { Button } from '@/src/shadComponents/ui/button';

interface SavePromptDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSaveAndExit: () => void;
    onExitWithoutSaving: () => void;
    isSaving?: boolean;
}

export default function SavePromptDialog({
    isOpen,
    onClose,
    onSaveAndExit,
    onExitWithoutSaving,
    isSaving = false,
}: SavePromptDialogProps) {
    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />

            {/* Dialog container */}
            <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
                <DialogPanel className="relative max-w-md w-full bg-background rounded-2xl shadow-2xl border border-border overflow-hidden">
                    {/* Close button */}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onClose}
                        disabled={isSaving}
                        className="absolute top-4 right-4"
                    >
                        <X className="h-5 w-5" />
                    </Button>

                    {/* Content */}
                    <div className="p-6 pt-8">
                        {/* Icon and title */}
                        <div className="flex flex-col items-center text-center mb-6">
                            <div className="flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
                                <AlertTriangle className="h-8 w-8 text-amber-600" />
                            </div>
                            <DialogTitle className="text-xl font-semibold text-foreground mb-2">
                                Save changes before exiting?
                            </DialogTitle>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                You have unsaved changes to your experience.
                                Would you like to save them before exiting the
                                editor?
                            </p>
                        </div>

                        {/* Action buttons */}
                        <div className="space-y-3">
                            {/* Primary action - Save & Exit */}
                            <Button
                                onClick={onSaveAndExit}
                                disabled={isSaving}
                                className="w-full"
                            >
                                {isSaving ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <FileUp className="h-5 w-5" />
                                        <span>Save & Exit</span>
                                    </>
                                )}
                            </Button>

                            {/* Secondary actions */}
                            <div className="grid grid-cols-2 gap-3">
                                <Button
                                    variant="destructive"
                                    onClick={onExitWithoutSaving}
                                    disabled={isSaving}
                                >
                                    Don't Save
                                </Button>

                                <Button
                                    variant="outline"
                                    onClick={onClose}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    );
}
